
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'
import Datepicker from '@/components/Datepicker.vue'
import { Vessel } from '@/models/Vessel'
import CompanyEditor from '@/components/Profile/CompanyEditor.vue'
import VesselEditor from '@/components/Profile/VesselEditor.vue'
import Notification from '@/classes/Notification'
import AttributeEditor from '@/components/Preferences/AttributeEditor.vue'
import axios from 'axios'
import { Company } from '@/models/Company'
import User from '@/models/User'
import { SingularResponse } from 'coloquent/dist/response/SingularResponse'
import i18n from '@/i18n'
import VesselDocuments from '@/components/Profile/VesselDocuments.vue'
import { namespace } from 'vuex-class'
import { Option } from 'coloquent/dist/Option'
import { Document } from '@/models/Document'
import FileTag from '@/enums/FileTag'

const VesselDetails = namespace('vesselDetails')

@Component({
	components: {
		VesselDocuments,
		AttributeEditor,
		VesselEditor,
		CompanyEditor,
		Datepicker
	}
})
export default class UnregisteredVesselCreator extends Vue {
	@Prop({
		type: Boolean,
		required: true,
		default: false
	})
	opened!: boolean

	@Prop({ required: false, default: null })
	unregisteredVessel!: Vessel | null

	@VesselDetails.Mutation('setVessel')
	setVesselState

	user!: User
	company: Company | null = null
	vessel: Vessel | null = null
	tab: string = 'vessel'

	isLoading: boolean = false
	isInitializing: boolean = false

	get isEditing() {
		return this.vessel && !!this.vessel.getApiId()
	}

	async created() {
		this.isInitializing = true
		this.setVesselState(null)
		const userQuery = User.fetchMeLazy()
		this.vessel = new Vessel()

		await this.fetchFreshVessel()

		this.user = await userQuery
		this.isInitializing = false
	}

	async fetchFreshVessel() {
		if (this.unregisteredVessel) {
			const companyQuery = this.unregisteredVessel.company().get()
			const vesselQuery = Vessel.option('include', 'vesselAttributes').find(
				this.unregisteredVessel.getApiId() as string
			)
			this.company = ((await companyQuery) as SingularResponse).getData() as Company
			this.vessel = (await vesselQuery).getData() as Vessel
		}
		this.setVesselState(this.vessel)
	}

	async refreshVesselWithAttributes(uploadedFile: Document) {
		if (uploadedFile.tag === FileTag.OTHER) return
		await this.fetchFreshVessel()
	}

	async submit(): Promise<void> {
		const companyEditor = this.$refs!.companyData! as CompanyEditor
		const vesselEditor = this.$refs!.vesselData! as VesselEditor
		const detailsEditor = this.$refs!.detailsEditor! as AttributeEditor
		const preferencesEditor = this.$refs!.preferencesEditor! as AttributeEditor

		const validCompany = await companyEditor.validate()
		const validVessel = await vesselEditor.validate()
		const validDetails = await detailsEditor.validate()
		const validPreferences = await preferencesEditor.validate()

		if (![validCompany, validVessel, validDetails, validPreferences].every(Boolean)) {
			if (!validCompany) {
				this.tab = 'company'
				return
			}
			if (!validVessel) {
				this.tab = 'vessel'
				return
			}
			if (!validDetails) {
				this.tab = 'vessel-details'
				return
			}
			if (!validPreferences) {
				this.tab = 'vessel-preferences'
				return
			}
		}

		this.isLoading = true
		try {
			const reference = companyEditor.reference
			const company = (await companyEditor.submit()) as Company
			vesselEditor.vessel.setCompany(company)
			const vesselQuery = vesselEditor.submit()

			let relationQuery = Promise.resolve(null as any)

			if (!this.isEditing) {
				relationQuery = axios.post(`${this.user.getCompany()!.getModelUrl()}/vessels`, {
					data: [
						{
							type: 'companies',
							id: company.getApiId(),
							attributes: {
								// need to pass the pivot values here
								reference: reference
							}
						}
					]
				})
			}

			const vessel = await vesselQuery

			await vessel.saveWithQuery([new Option('include', 'vesselAttributes')])

			await Vessel.option('include', 'vesselAttributes').find(vessel.getApiId() as string)
			await relationQuery

			await Notification.Success(i18n.t('profile.edittedSuccessfully'))
			this.$emit('vesselCreated', vessel)
		} finally {
			this.isLoading = false
		}
	}
}
